import { ProgressEvent } from '../adapters';
import {
    MediaControllerState,
    MediaControllerStateSnapshot,
    PlanetMediaPlayer,
    PlaybackSession,
    PlaybackSessionState,
    PlayerPlaylistArgs,
    PlayerPlaylistItemChangeArgs,
    PlayerPositionArgs,
    PlayerStateChangeArgs,
} from '../media-player';
import { MediaItem, PlanetMediaPlayerEvents } from '../models';
import { EventEmitterBase, IDisposable } from '../utils';
import { IPlugin, OnDispose, OnPlaybackSession, OnPlaylist, OnReady, OnSeek, OnStateChange } from './models';

export abstract class AbstractPlugin<TOptions> extends EventEmitterBase
    implements IPlugin<TOptions>,
        OnPlaybackSession,
        OnSeek,
        OnPlaylist,
        OnStateChange,
        OnDispose,
        OnReady,
        OnStateChange,
        IDisposable {

    protected player: PlanetMediaPlayer;
    protected options: TOptions;

    protected seekSession: { from: number, to: number } = null;

    async setup(player: PlanetMediaPlayer, options: TOptions): Promise<void> {

        this.player = player;

        this.options = options;

        this.player.once(PlanetMediaPlayerEvents.Ready, () => this.onReady());
        this.player.on(PlanetMediaPlayerEvents.Playlist, (e: PlayerPlaylistArgs) => this.onPlaylist(e.playlist));
        this.player.on(PlanetMediaPlayerEvents.PlaylistItem, (e: PlayerPlaylistItemChangeArgs) => this.onPlaylistItem(e.item, e.index));
        this.player.on(PlanetMediaPlayerEvents.StateChange, (e: PlayerStateChangeArgs) => this.onStateChange(e.newState, e.oldState, this.player.getStateSnapshot()));
        this.player.on(PlanetMediaPlayerEvents.Remove, () => this.onDispose());
        this.player.on(PlanetMediaPlayerEvents.Seek, () => {
        });
        this.player.on(PlanetMediaPlayerEvents.Seeked, (e: PlayerPositionArgs) => this.onSeeked(e.position));
        this.player.on(PlanetMediaPlayerEvents.BufferChange, (e: ProgressEvent) => this.onBufferChange(e));
        this.player.on(PlanetMediaPlayerEvents.Complete, () => this.onComplete());
        this.player.on(PlanetMediaPlayerEvents.PlaybackSessionCreate, (session: PlaybackSession) => this.onPlaybackSessionCreate(session.state));

        this.player.on(PlanetMediaPlayerEvents.PlaybackSessionStart, (session: PlaybackSessionState) => this.onPlaybackSessionStart(session));
        this.player.on(PlanetMediaPlayerEvents.PlaybackSessionPause, (session: PlaybackSessionState) => this.onPlaybackSessionPause(session));
        this.player.on(PlanetMediaPlayerEvents.PlaybackSessionResume, (session: PlaybackSessionState) => this.onPlaybackSessionResume(session));
        this.player.on(PlanetMediaPlayerEvents.PlaybackSessionSeek, (session: PlaybackSessionState) => this.onPlaybackSessionSeek(session));
        this.player.on(PlanetMediaPlayerEvents.PlaybackSessionEnd, (session: PlaybackSessionState) => this.onPlaybackSessionEnd(session));
        this.player.on(PlanetMediaPlayerEvents.PlaybackSessionContentLoad, (session: PlaybackSessionState) => this.onPlaybackSessionContentLoad(session));
    }

    abstract onReady(): void;

    abstract onDispose(): void;

    onSeek(from, to): void {
        this.seekSession = {from, to};
    }

    abstract onSeeked(position: number): void;

    abstract onBufferChange(data: ProgressEvent): void;

    abstract onPlaylist(playlist: MediaItem[]): void;

    abstract onPlaylistItem(item: MediaItem, index: number): void;

    abstract onComplete(): void;

    abstract dispose(): void;

    abstract onStateChange(state: MediaControllerState, previousState: MediaControllerState, stateSnapshot: MediaControllerStateSnapshot): void;

    abstract onPlaybackSessionCreate(session: PlaybackSessionState): void;

    abstract onPlaybackSessionContentLoad(session: PlaybackSessionState): void;

    abstract onPlaybackSessionEnd(session: PlaybackSessionState): void;

    abstract onPlaybackSessionPause(session: PlaybackSessionState): void;

    abstract onPlaybackSessionResume(session: PlaybackSessionState): void;

    abstract onPlaybackSessionSeek(session: PlaybackSessionState): void;

    abstract onPlaybackSessionStart(session: PlaybackSessionState): void;
}
